import { inject, Injectable } from '@angular/core';
import { ChartThemeService } from '@app/core/services/charts/chart-theme.service';
import { BorderRadius, ChartDataset } from 'chart.js';

export interface ChartDatasetConfig<T extends object = object> {
  label: string;
  tooltipLabel?: string;
  dataValue: (el: T) => number | { x: number; y: number } | null;
  type?: 'line' | 'bar';
  backgroundColor?: string | ((item: T, index: number) => string);
  hoverBackgroundColor?: string | ((item: T, index: number) => string);
  borderColor?: string | ((item: T, index: number) => string);
  borderWidth?: number;
  minBarLength?: number;
  categoryPercentage?: number;
  barPercentage?: number;
  borderRadius?: number | BorderRadius;
  maxBarThickness?: number;
  fill?: boolean;

  pointRadius?: number;
  pointHoverRadius?: number;
  pointBackgroundColor?: string | ((item: T, index: number) => string);
  pointBorderColor?: string | ((item: T, index: number) => string);
  pointBorderWidth?: number;
}

export type ExtendedChartDataset<T> = ChartDataset<'bar' | 'line'> & {
  meta?: T[];
  tooltipLabel?: string;
};

@Injectable({
  providedIn: 'root',
})
export class ChartDataService {
  protected chartThemeService = inject(ChartThemeService);

  generateDatasets<T extends object = object>(data: T[], configs: ChartDatasetConfig<T>[]): ExtendedChartDataset<T>[] {
    return configs.map(config => {
      const dataset: ExtendedChartDataset<T> = {
        label: config.label,
        tooltipLabel: config.tooltipLabel,
        type: config.type,
        data: data.map(item => config.dataValue(item)),
        meta: data,
        backgroundColor: data.map((item, index) =>
          typeof config.backgroundColor === 'function'
            ? config.backgroundColor(item, index)
            : config.backgroundColor || this.chartThemeService.getColors().datasetBackgroundColor
        ),
        hoverBackgroundColor: config.hoverBackgroundColor
          ? data.map((item, index) =>
              typeof config.hoverBackgroundColor === 'function'
                ? config.hoverBackgroundColor(item, index)
                : config.hoverBackgroundColor
            )
          : undefined,
        borderColor: data.map((item, index) =>
          typeof config.borderColor === 'function'
            ? config.borderColor(item, index)
            : config.borderColor || this.chartThemeService.getColors().datasetColor
        ),
        borderWidth: config.borderWidth ?? 2,
        minBarLength: config.minBarLength ?? undefined,
        borderSkipped: 'middle',
        barPercentage: config.barPercentage ?? 0.5,
        categoryPercentage: config.categoryPercentage ?? 1.0,
        maxBarThickness: config.maxBarThickness,
        // for line
        fill: config.fill,
        pointRadius: config.pointRadius,
        pointHoverRadius: config.pointHoverRadius,
        pointBackgroundColor:
          config.pointBackgroundColor &&
          data.map((item, index) =>
            typeof config.pointBackgroundColor === 'function'
              ? config.pointBackgroundColor(item, index)
              : config.pointBackgroundColor || this.chartThemeService.getColors().datasetColor
          ),
        pointBorderColor:
          config.pointBorderColor &&
          data.map((item, index) =>
            typeof config.pointBorderColor === 'function'
              ? config.pointBorderColor(item, index)
              : config.pointBorderColor || this.chartThemeService.getColors().datasetColor
          ),
      };
      return dataset;
    });
  }
}
